import AppContext from "@/AppContext";
import Layout from "@/components/elements/Layout";
import useAuth from "@/hooks/auth";
import { useIsMobile } from "@/hooks/useIsMobile";
import { cn } from "@/lib/utils";
import {
  BookmarkSimple,
  ClockCounterClockwise,
  CreditCard,
  FileText,
  Gear,
  Heart,
  LockKey,
  PersonArmsSpread,
  RocketLaunch,
  Sparkle,
  UserCircle,
  ChartLineUp,
} from "@phosphor-icons/react";
import {
  ChatCircleDots,
  ChatTeardropDots,
  Tray,
} from "@phosphor-icons/react/dist/ssr";
import { Building } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useContext } from "react";

export default function AccountLayout({ children, title }) {
  const { user } = useAuth({ middleware: "auth" });
  const { asPath } = useRouter();
  const appContext = useContext(AppContext);
  const { planSubscription, loading, refreshUserSubscription } =
    appContext.state;

  let accounts = [
    {
      link: "/account/profile",
      name: "Profile",
      icon: <UserCircle weight='duotone' className='mr-2 text-lg opacity-50' />,
    },
    {
      link: "/account/password",
      name: "Change Password",
      icon: <LockKey weight='duotone' className='mr-2 text-lg opacity-50' />,
    },
    {
      link: "/messages",
      name: "My Messages",
      icon: (
        <ChatCircleDots weight='duotone' className='mr-2 text-lg opacity-50' />
      ),
    },
  ];

  const contents = [
    {
      link: "/account/preferences",
      name: "Preferences",
      icon: <Gear weight='duotone' className='mr-2 text-lg opacity-50' />,
    },
    {
      link: "/account/bookmarks",
      name: "Bookmarks",
      icon: (
        <BookmarkSimple weight='duotone' className='mr-2 text-lg opacity-50' />
      ),
    },
    {
      link: "/account/watchlist",
      name: "Watchlist",
      icon: <Heart weight='duotone' className='mr-2 text-lg opacity-50' />,
    },
    {
      link: "/account/report-history",
      name: "Special Reports",
      icon: (
        <ClockCounterClockwise
          weight='duotone'
          className='mr-2 text-lg opacity-50'
        />
      ),
    },
  ];
  const payments = [
    {
      link: "/account/plan",
      name: "My Plan",
      icon: <UserCircle weight='duotone' className='mr-2 text-lg opacity-50' />,
      active: true,
    },
    {
      link: "/account/usage",
      name: "Usage",
      icon: <Tray weight='duotone' className='mr-2 text-lg opacity-50' />,
      active: !planSubscription?.has_active_subscription,
    },
    {
      link: "/account/payment",
      name: "Payment",
      icon: <CreditCard weight='duotone' className='mr-2 text-lg opacity-50' />,
      active: true,
    },
    {
      link: "/account/billing",
      name: "Billing",
      icon: <FileText weight='duotone' className='mr-2 text-lg opacity-50' />,
      active: true,
    },
  ];

  const ownerships = [
    {
      link: "/account/startup",
      name: "My Startup",
      icon: (
        <RocketLaunch weight='duotone' className='mr-2 text-lg opacity-50' />
      ),
      show: planSubscription?.is_startup_plan,
    },
    {
      link: "/account/vc",
      name: "My VC",
      icon: (
        <Building
          weight='duotone'
          className='w-4 h-4 mr-2 text-lg opacity-50'
        />
      ),
      show: planSubscription?.is_vc_plan,
    },
    {
      link: "/account/angel",
      name: "My Angel",
      icon: (
        <PersonArmsSpread
          weight='duotone'
          className='mr-2 text-lg opacity-50'
        />
      ),
      show: planSubscription?.is_angel_plan,
    },
  ];

  const manageAccesses = [
    {
      link: "/account/access-startup",
      name: "Startup",
      icon: <Sparkle weight='duotone' className='mr-2 text-lg opacity-50' />,
      active: planSubscription?.has_startup,
    },
    {
      link: "/account/access-investor",
      name: "VC",
      icon: <Sparkle weight='duotone' className='mr-2 text-lg opacity-50' />,
      active: planSubscription?.has_vc,
    },
    {
      link: "/account/access-angel-investor",
      name: "Angel Investor",
      icon: <Sparkle weight='duotone' className='mr-2 text-lg opacity-50' />,
      active: planSubscription?.has_angel_vc,
    },
  ];

  const myAccesses = [
    {
      link: "/account/my-accesses",
      name: "My Accesses",
      icon: <Sparkle weight='duotone' className='mr-2 text-lg opacity-50' />,
    },
  ];

  const isAdmin =
    user?.roles === "admin" ||
    user?.roles === "admin-team" ||
    (Array.isArray(user?.roles) &&
      (user.roles.includes("admin") || user.roles.includes("admin-team")));

  const adminTools = [
    {
      link: "/account/admin",
      name: "Admin",
      icon: <Gear weight='duotone' className='mr-2 text-lg opacity-50' />,
    },
    {
      link: "/account/vc-claim-by-admin",
      name: "VC Claim",
      icon: <Sparkle weight='duotone' className='mr-2 text-lg opacity-50' />,
    },
    {
      link: "/account/analysis",
      name: "Analysis",
      icon: <ChartLineUp weight='duotone' className='mr-2 text-lg opacity-50' />,
    },
  ];

  const isMobile = useIsMobile();

  return (
    <Layout title={title} hideBottomMobileNavigation>
      <div className='px-4 lg:container lg:h-full lg:flex lg:gap-6'>
        {!isMobile && (
          <div className='sticky max-h-[calc(100vh-148px)] w-56 h-full pt-4 -ml-3 space-y-6 block py-8 overflow-y-auto no-scrollbar'>
            <div>
              <span className='block mb-2 ml-3 text-sm opacity-60'>
                Account
              </span>
              <div className='space-y-0.5'>
                {accounts.map((item, index) => (
                  <Link
                    legacyBehavior
                    href={item.link}
                    key={`account-${index}`}
                  >
                    <a
                      className={cn(
                        "flex items-center px-3 py-2 rounded-md transition-colors duration-300 hover:bg-white/5",
                        asPath === item.link &&
                          "border-t shadow-sm bg-gradient-to-b from-white/[0.11] to-white/[0.07] border-white/10",
                      )}
                    >
                      {item.icon}
                      {item.name}
                    </a>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <span className='block mb-2 ml-3 text-sm opacity-60'>
                Content
              </span>
              <div className='space-y-0.5'>
                {contents.map((item, index) => (
                  <Link
                    legacyBehavior
                    href={item.link}
                    key={`content-${index}`}
                  >
                    <a
                      className={cn(
                        "flex items-center px-3 py-2 rounded-md transition-colors duration-300 hover:bg-white/5",
                        asPath === item.link &&
                          "border-t shadow-sm bg-gradient-to-b from-white/[0.11] to-white/[0.07] border-white/10",
                      )}
                    >
                      {item.icon}
                      {item.name}
                    </a>
                  </Link>
                ))}
              </div>
            </div>
            <div>
              <span className='block mb-2 ml-3 text-sm opacity-60'>
                Subscription
              </span>
              <div className='space-y-0.5'>
                {payments
                  .filter((item) => item.active)
                  .map((item, index) => (
                    <Link
                      legacyBehavior
                      href={item.link}
                      key={`payment-${index}`}
                    >
                      <a
                        className={cn(
                          "flex items-center px-3 py-2 rounded-md transition-colors duration-300 hover:bg-white/5",
                          asPath === item.link &&
                            "border-t shadow-sm bg-gradient-to-b from-white/[0.11] to-white/[0.07] border-white/10",
                        )}
                      >
                        {item.icon}
                        {item.name}
                      </a>
                    </Link>
                  ))}
              </div>
            </div>
            <div>
              <span className='block mb-2 ml-3 text-sm opacity-60'>
                Ownership
              </span>
              <div className='space-y-0.5'>
                {ownerships
                  .filter((item) => item.show)
                  .map((item, index) => (
                    <Link
                      href={item.link}
                      key={`startup-${index}`}
                      className={cn(
                        "flex items-center px-3 py-2 rounded-md transition-colors duration-300 hover:bg-white/5",
                        asPath === item.link &&
                          "border-t shadow-sm bg-gradient-to-b from-white/[0.11] to-white/[0.07] border-white/10",
                      )}
                    >
                      {item.icon}
                      {item.name}
                    </Link>
                  ))}
              </div>
            </div>
            {(planSubscription?.has_startup || planSubscription?.has_vc) && (
              <div>
                <span className='block mb-2 ml-3 text-sm opacity-60'>
                  Manage Access
                </span>
                <div className='space-y-0.5'>
                  {manageAccesses
                    .filter((m) => m.active)
                    .map((item, index) => (
                      <Link
                        legacyBehavior
                        href={item.link}
                        key={`investor-${index}`}
                      >
                        <a
                          className={cn(
                            "flex items-center px-3 py-2 rounded-md transition-colors duration-300 hover:bg-white/5",
                            asPath === item.link &&
                              "border-t shadow-sm bg-gradient-to-b from-white/[0.11] to-white/[0.07] border-white/10",
                          )}
                        >
                          {item.icon}
                          {item.name}
                        </a>
                      </Link>
                    ))}
                </div>
              </div>
            )}
            <div>
              <span className='block mb-2 ml-3 text-sm opacity-60'>Access</span>
              <div className='space-y-0.5'>
                {myAccesses.map((item, index) => (
                  <Link
                    legacyBehavior
                    href={item.link}
                    key={`investor-${index}`}
                  >
                    <a
                      className={cn(
                        "flex items-center px-3 py-2 rounded-md transition-colors duration-300 hover:bg-white/5",
                        asPath === item.link &&
                          "border-t shadow-sm bg-gradient-to-b from-white/[0.11] to-white/[0.07] border-white/10",
                      )}
                    >
                      {item.icon}
                      {item.name}
                    </a>
                  </Link>
                ))}
              </div>
            </div>
            {isAdmin && (
              <div>
                <span className='block mb-2 ml-3 text-sm opacity-60'>
                  Admin
                </span>
                <div className='space-y-0.5'>
                  {adminTools.map((item, index) => (
                    <Link
                      legacyBehavior
                      href={item.link}
                      key={`admin-tool-${index}`}
                    >
                      <a
                        className={cn(
                          "flex items-center px-3 py-2 rounded-md transition-colors duration-300 hover:bg-white/5",
                          asPath === item.link &&
                            "border-t shadow-sm bg-gradient-to-b from-white/[0.11] to-white/[0.07] border-white/10",
                        )}
                      >
                        {item.icon}
                        {item.name}
                      </a>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        <div
          className={cn(
            "w-full min-w-0 lg:flex-1 lg:h-[calc(100vh-148px)]",
            asPath !== "/messages" && "mt-5",
          )}
        >
          {children}
        </div>
      </div>
    </Layout>
  );
}
